import styled from 'styled-components';
import { useContext, useEffect, useRef, useState } from 'react';

import { CustomEditorV2Readonly } from '@/component/customEditorV2';
import { Question, QuizViewContext } from '@/component/customEditorV2/ui/elements/QuizQuestionElement/View/QuizViewContext';
import { useMyLearningActivityQuizSubmissionsQueryRemote } from '@/store/v2';
import { RetakeModal } from '@/administration/pages/Course/pages/quiz/View/ViewQuizPage/RetakeModal';
import { CourseNavigationContext } from '@/administration/pages/Course/MainLayoutView/atoms/NavigationActions/CourseNavigationProvider';
import { QuizBannerElement } from '@/administration/pages/Course/pages/quiz/View/ViewQuizPage/QuizBannerElement';

const RootStyled = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.primary.onMain};
  color: ${({ theme }) => theme.colors.neutral.onContainer};
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0px 28px;
  border-radius: 20px;
  height: 100%;
  min-height: calc(100vh - 285px);
  justify-content: flex-start;
  align-items: flex-start;

  @media (min-width: ${(p) => p.theme.breakpoints.desktopLarge.end}px) {
    align-items: center;
  }
`;

const ContentStyled = styled.div`
  width: 100%;
  max-width: 776px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  word-wrap: break-word;
  overflow-x: auto;
  gap: 24px;
`;

type ViewQuizPageProps = {
  id: string;
  text: string;
  passingCriteria?: number;
  totalQuestionsCount: number;
  onCompleted?: () => void;
  spaceId: number;
  remoteId: number;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ViewQuizPage = ({
  id,
  text,
  spaceId,
  onCompleted,
  remoteId,
  passingCriteria,
  totalQuestionsCount,
}: ViewQuizPageProps): JSX.Element | null => {
  const [retakeModalOpen, setRetakeModalOpen] = useState(false);
  const { data, loading, refetch } = useMyLearningActivityQuizSubmissionsQueryRemote({
    variables: {
      input: {
        includeOlderVersions: true,
        learningActivityQuizIdsIN: [remoteId],
      },
    },
  });
  const [answeredQuestions, setAnsweredQuestions] = useState<Question[]>([]);
  const [score, setScore] = useState(0);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [hasPassed, setHasPassed] = useState(false);
  const { setIsPageLoading, setQuizSubmitted, setQuizHasPassed } = useContext(CourseNavigationContext);

  useEffect(() => {
    setIsPageLoading(loading);
  }, [loading]);

  const completionTriggeredRef = useRef(false);

  useEffect(() => {
    if (hasPassed && !completionTriggeredRef.current) {
      completionTriggeredRef.current = true;
      onCompleted && onCompleted();
    }
  }, [hasPassed, onCompleted]);

  useEffect(() => {
    setQuizSubmitted(submitted);
  }, [submitted, setQuizSubmitted]);

  useEffect(() => {
    setQuizHasPassed(hasPassed);
  }, [hasPassed, setQuizHasPassed]);

  useEffect(() => {
    return () => {
      setQuizSubmitted(false);
      setQuizHasPassed(false);
    };
  }, [setQuizSubmitted, setQuizHasPassed]);

  const handleRetakeConfirmed = () => {
    setRetakeModalOpen(false);
    setSubmitted(false);
    setAnsweredQuestions([]);
    setHasPassed(false);
    completionTriggeredRef.current = false;
  };

  useEffect(() => {
    if (data?.myLearningActivityQuizSubmissions.length) {
      setAnsweredQuestions(() => {
        const answers = (data.myLearningActivityQuizSubmissions[0].answers as any[]).map((answer) => ({
          questionId: answer.questionId as string,
          answersIds: new Set(answer.selectedOptions.map((option: any) => (option as any).id)) as Set<string>,
        }));

        return answers;
      });

      setCorrectAnswersCount(data.myLearningActivityQuizSubmissions[0].correctlyAnsweredCount);
      setScore(Math.round(((data.myLearningActivityQuizSubmissions[0].score ?? 0) as number) * 100));
      setSubmitted(true);
      setHasPassed(data.myLearningActivityQuizSubmissions[0].hasPassed);
    }
  }, [loading, data?.myLearningActivityQuizSubmissions]);

  if (loading) return null;

  return (
    <RootStyled key={id}>
      <ContentStyled>
        <QuizViewContext.Provider
          value={{
            answeredQuestions: answeredQuestions,
            setAnsweredQuestions,
            learningActivityId: remoteId,
            submitted,
            refetch,
            retakeModalOpen,
            setRetakeModalOpen,
          }}
        >
          <RetakeModal onClose={() => setRetakeModalOpen(false)} onRetake={handleRetakeConfirmed} open={retakeModalOpen} />
          <QuizBannerElement
            hasPassingCriteria={Boolean(passingCriteria)}
            passingCriteria={(passingCriteria ?? 0) * 100}
            hasSubmitted={submitted}
            hasPassed={hasPassed}
            correctAnswersCount={correctAnswersCount}
            totalQuestionsCount={totalQuestionsCount}
            score={score}
          />
          <CustomEditorV2Readonly
            value={text}
            style={{ fontSize: 14 }}
            toggledFeatures={{ trimEndEmptyParagraphs: true }}
            $fullSize
            minimized
            spaceId={spaceId}
          />
        </QuizViewContext.Provider>
      </ContentStyled>
    </RootStyled>
  );
};
